'use client'

import { PartnerHospitalSchedule } from "@/entities/hospital/api/types"
import { DirectionIcon, MapPinIcon, TextButton, Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import { NaverMap } from "@/shared/ui/organisms"
import { toast } from "@/shared/utils/toastMessage"
import Image from "next/image"
import { useCallback, useEffect, useState } from "react"
import { HospitalMapViewerModal } from "@/widgets/hospital/ui/molecules"
import { useTranslations } from "next-intl"

interface HospitalAddressProps {
  addressProps: {
    todaySchedule?: PartnerHospitalSchedule
    weeklySchedules: PartnerHospitalSchedule[]
    tel: string
    fullAddress: string
    location: {
      latitude: number
      longitude: number
    }
  }
  initIsOpen: boolean
}

const HospitalAddress = ({ addressProps, initIsOpen }: HospitalAddressProps) => {
  const t = useTranslations()
  const { todaySchedule, weeklySchedules, tel, fullAddress, location } = addressProps
  const [isOpenSchedul, setIsOpenSchedul] = useState(false)
  const [isOpen, setIsOpen] = useState(initIsOpen)
  const [isMapOpen, setIsMapOpen] = useState(false)

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress)
    toast.success(t("common.copySuccess"))
  }

  const checkIsOpen = useCallback(() => {
    if(!todaySchedule) return false

    const [startTimeHour, startTimeMinute] = todaySchedule.startTime.split(':').map(Number)
    const [endTimeHour, endTimeMinute] = todaySchedule.endTime.split(':').map(Number)
    const now = new Date()
    const startTime = new Date(now.getFullYear(),now.getMonth(),now.getDate(),startTimeHour,startTimeMinute,0)
    const endTime = new Date(now.getFullYear(),now.getMonth(),now.getDate(),endTimeHour,endTimeMinute,0)

    return startTime < now && endTime > now
  }, [todaySchedule])

  const formatWeeklyScheduleDay = (day: string) => {
    return day.replace('요일', '')
  }

  useEffect(() => {
    const second = new Date().getSeconds()
    const timeToNextMinute = second === 0 ? 0 : 59 - second

    let interval: ReturnType<typeof setInterval> | undefined
    const setTimer = setTimeout(() => {
      setIsOpen(checkIsOpen())
      interval = setInterval(() => {
        setIsOpen(checkIsOpen())
      }, 60000)
    }, timeToNextMinute * 1000)

    return () => {
      clearTimeout(setTimer)
      if(interval) clearInterval(interval)
    }
  }, [checkIsOpen])

  return (
    <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
      <div className="flex flex-col gap-1">
        <div className="flex items-center">
          <Image src="/icons/clock.svg" alt="clock" width={16} height={16} className="mr-2" />
          <Typography size={14} lineHeight={18} className={`mr-1 font-semibold ${isOpen ? 'text-primary-700' : 'text-gray-400'}`}>
            {isOpen ? t("address.open") : t("address.closed")}
          </Typography>
          <CustomButton className="flex items-center gap-1" onClick={() => setIsOpenSchedul(!isOpenSchedul)}>
            <Typography size={14} lineHeight={18} className="text-gray-700">{todaySchedule?.dayType} {todaySchedule?.startTime} - {todaySchedule?.endTime}</Typography>
            <DirectionIcon size={16} className="text-gray-700" rotate={isOpenSchedul ? 180 : 0} />
          </CustomButton>
        </div>
        {isOpenSchedul && <div className="flex flex-col gap-1 pl-[22px]">
          {weeklySchedules.map((schedule) => (
            <div key={schedule.dayType}>
              <Typography size={14} lineHeight={18} className={`text-gray-700 ${schedule.dayType === todaySchedule?.dayType && 'font-semibold'}`}>{formatWeeklyScheduleDay(schedule.dayType)} {schedule.startTime} - {schedule.endTime}</Typography>
            </div>
          ))}
        </div>}
      </div>
      <div className="flex items-center">
        <Image src="/icons/call.svg" alt="clock" width={16} height={16} className="mr-2" />
        <Typography size={14} lineHeight={18} className="text-gray-700">
          {tel}
        </Typography>
      </div>
      <div className="flex items-start">
        <MapPinIcon size={16} className="text-gray-300 flex-shrink-0 mr-2" />
        <Typography size={14} lineHeight={18} className="text-gray-700">
          {fullAddress}
        </Typography>
        <TextButton text={t("common.copy")} size={12} className="mr-2 flex-shrink-0 text-gray-500 leading-[18px] underline decoration-gray-500 underline-offset-[3px]" onClick={handleCopyAddress} />
      </div>
      {location && <div className="w-full h-[100px] mt-2">
        <div className="relative w-full h-full">
          <NaverMap location={location} className="rounded-lg" />
          <button
            type="button"
            aria-label={t("common.openMapViewer")}
            className="absolute inset-0"
            onClick={() => setIsMapOpen(true)}
          />
        </div>
      </div>}

      <HospitalMapViewerModal open={isMapOpen} onClose={() => setIsMapOpen(false)} location={location} />
    </div>
  )
}

export default HospitalAddress

